<template>
  <!-- Section Separator -->
  <div class="section-separator">
    <div class="separator-content">
      <div class="separator-line left"></div>
      <div class="separator-emblem">
        <div class="emblem-outer"></div>
        <div class="emblem-inner">
          <div class="emblem-icon"></div>
        </div>
      </div>
      <div class="separator-line right"></div>
    </div>
    <div class="separator-triangles">
      <div class="triangle-left"></div>
      <div class="triangle-right"></div>
    </div>
  </div>

  <div class="take-a-tour-container py-20" ref="tourContainer">
    <div class="background-pattern"></div>
    
    <!-- Floating Images Background -->
    <div class="floating-images">
      <div v-for="(image, index) in tourImages" :key="index" 
           class="floating-image" 
           :class="`image-${index}`"
           :style="{ 
             backgroundImage: `url(${image})`,
             transform: getImageTransform(index)
           }">
      </div>
    </div>
    
    <!-- Main Content with Glassmorphism -->
    <div class="content-container" 
         ref="contentCard"
         :class="{ 'visible': isInView }"
         :style="{ transform: `perspective(1500px) rotateY(${cardRotation.x}deg) rotateX(${cardRotation.y}deg)` }">
      <div class="glassmorphism-card">
        <div class="card-accent top-left"></div>
        <div class="card-accent top-right"></div>
        <div class="card-accent bottom-left"></div>
        <div class="card-accent bottom-right"></div>
        
        <div class="gradient-overlay"></div>
        <div class="content-inner">
          <h2 class="title">Experience Our Facility</h2>
          <p class="subtitle">American craftsmanship meets cutting-edge technology</p>
          
          <div class="video-preview" @click="openModal" @mousemove="handlePreviewMouseMove">
            <div class="video-thumbnail" :style="{ backgroundImage: 'url(/img/TakeaTour.jpg)' }">
              <div class="play-button-wrapper">
                <div class="play-button-outer">
                  <div class="play-button-inner">
                    <font-awesome-icon :icon="['fas', 'play']" class="play-icon" />
                  </div>
                </div>
              </div>
            </div>
            <div class="video-gradient"></div>
          </div>
          
          <button class="cta-button" @click="openModal" @mouseover="btnHovered = true" @mouseleave="btnHovered = false">
            <span class="button-text" :class="{ 'button-hovered': btnHovered }">WATCH OUR GRAND OPENING WITH PRESIDENT TRUMP</span>
            <div class="button-background" :class="{ 'button-bg-animate': btnHovered }"></div>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Steel Accent Elements -->
    <div class="steel-accent left"></div>
    <div class="steel-accent right"></div>
    <div class="steel-accent top"></div>
    <div class="steel-accent bottom"></div>
    
    <!-- Video Modal -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <button class="close-button" @click="closeModal">
            <font-awesome-icon :icon="['fas', 'rectangle-xmark']" />
          </button>
          <div class="video-container">
            <iframe 
              width="100%" 
              height="100%" 
              :src="youtubeUrl" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'TakeATour',
  data() {
    return {
      showModal: false,
      youtubeUrl: 'https://player.vimeo.com/video/1129956055?h=f04c50581b', // Replace with your actual video ID
      isInView: false,
      observer: null,
      tourImages: [
        '/img/TourSmall_1.jpg',
        '/img/TourSmall_2.jpg',
        '/img/TourSmall_3.jpg',
        '/img/TourSmall_4.jpg',
        '/img/TourSmall_5.jpg',
        '/img/TourSmall_6.jpg',
        '/img/TourSmall_7.jpg'
      ],
      btnHovered: false,
      parallaxImages: false,
      parallaxOffsets: [],
      cardRotation: { x: 0, y: 0 },
      mousePosition: { x: 0, y: 0 },
      initialLoad: true
    };
  },
  created() {
    // Initialize parallax offsets
    this.tourImages.forEach(() => {
      this.parallaxOffsets.push({ x: 0, y: 0 });
    });
  },
  mounted() {
    this.initIntersectionObserver();
    
    // Add event listeners for mouse movement only after initial animation is complete
    setTimeout(() => {
      this.initialLoad = false;
      if (window.innerWidth > 768) { // Only enable on desktop/tablet
        this.parallaxImages = true;
        window.addEventListener('mousemove', this.handleMouseMove);
      }
    }, 100);
    
    // Initialize with entrance animation
    setTimeout(() => {
      this.isInView = true;
    }, 300);
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    window.removeEventListener('mousemove', this.handleMouseMove);
  },
  methods: {
    openModal() {
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      document.body.classList.remove('modal-open');
    },
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.2
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.isInView = true;
          } else {
            // Keep visible once shown
            // this.isInView = false;
          }
        });
      }, options);
      
      this.observer.observe(this.$refs.tourContainer);
    },
    handleMouseMove(event) {
      // Skip parallax calculations during initial load
      if (this.initialLoad) return;
      
      const container = this.$refs.tourContainer;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - rect.width / 2;
      const mouseY = event.clientY - rect.top - rect.height / 2;
      
      // Update image positions for parallax effect
      this.parallaxOffsets = this.tourImages.map((_, index) => {
        const factor = (index % 3 + 1) * 0.03;
        return {
          x: mouseX * factor * -1,
          y: mouseY * factor * -1
        };
      });
      
      // Update card rotation if the content card exists
      if (this.$refs.contentCard) {
        const cardRect = this.$refs.contentCard.getBoundingClientRect();
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        
        // Calculate rotation based on mouse position (limited range)
        this.cardRotation = {
          x: ((event.clientX - centerX) / cardRect.width * 1),
          y: ((event.clientY - centerY) / cardRect.height * -1)
        };
      }
    },
    handlePreviewMouseMove(event) {
      const preview = event.currentTarget;
      const bounds = preview.getBoundingClientRect();
      const mouseX = event.clientX - bounds.left;
      const mouseY = event.clientY - bounds.top;
      
      // Calculate movement percentages
      const moveX = (mouseX / bounds.width - 0.5) * 1;
      const moveY = (mouseY / bounds.height - 0.5) * 1;
      
      // Apply a subtle transform to the video thumbnail for a 3D hover effect
      preview.style.transform = `perspective(1500px) rotateY(${moveX}deg) rotateX(${-moveY}deg) scale3d(1.05, 1.05, 1.05)`;
      
      // Reset the transform when mouse leaves
      preview.addEventListener('mouseleave', () => {
        preview.style.transform = 'perspective(1500px) rotateY(0) rotateX(0) scale3d(1, 1, 1)';
      }, { once: true });
    },
    
    // New method to handle image transforms properly
    getImageTransform(index) {
      // During initial load, only use the base transform without parallax
      if (this.initialLoad) {
        return null; // Let CSS handle initial position
      }
      
      // After initial load, combine base transform with parallax
      const baseTransforms = [
        'rotate(-5deg) scale(0.8)',
        'rotate(3deg) scale(0.7)',
        'rotate(8deg) scale(0.75)',
        'rotate(-7deg) scale(0.7)',
        'rotate(5deg) scale(0.6) translateY(-50%)',
        'rotate(-4deg) scale(0.65) translateY(-50%)',
        'rotate(6deg) scale(0.55)'
      ];
      
      const baseTransform = baseTransforms[index];
      
      if (!this.parallaxImages) {
        return baseTransform;
      }
      
      // Add parallax offset to base transform
      const offsetX = this.parallaxOffsets[index].x;
      const offsetY = this.parallaxOffsets[index].y;
      return `translate(${offsetX}px, ${offsetY}px) ${baseTransform}`;
    }
  }
};
</script>

<style scoped>
/* Section Separator Styling */
.section-separator {
  position: relative;
  width: 100%;
  height: 120px;
  margin-top: -60px;
  margin-bottom: 30px;
  z-index: 5;
  pointer-events: none;
  overflow: visible;
}

.separator-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 30px;
}

.separator-line {
  height: 3px;
  background: linear-gradient(90deg, transparent, #8f0000, rgba(183, 185, 186, 0.8));
  width: 25%;
  max-width: 300px;
  position: relative;
}

.separator-line.left {
  background: linear-gradient(90deg, transparent, rgba(183, 185, 186, 0.8), #8f0000);
}

.separator-line.right {
  background: linear-gradient(90deg, #8f0000, rgba(183, 185, 186, 0.8), transparent);
}

.separator-line::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 10%;
  right: 10%;
  height: 10px;
  background: repeating-linear-gradient(
    90deg,
    rgba(183, 185, 186, 0.2),
    rgba(183, 185, 186, 0.2) 8px,
    transparent 8px,
    transparent 16px
  );
}

.separator-triangles {
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: center;
  z-index: 2;
}

.triangle-left, .triangle-right {
  width: 40%;
  height: 100%;
  background: linear-gradient(to bottom right, #1d1e26, transparent);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.triangle-right {
  background: linear-gradient(to bottom left, #1d1e26, transparent);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.separator-emblem {
  width: 90px;
  height: 90px;
  position: relative;
  margin: 0 15px;
}

.emblem-outer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid #8f0000;
  border-radius: 50%;
  animation: rotate 30s linear infinite;
  box-shadow: 0 0 15px rgba(143, 0, 0, 0.3);
}

.emblem-outer::before, .emblem-outer::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgba(183, 185, 186, 0.8);
  border-radius: 50%;
  top: calc(50% - 5px);
}

.emblem-outer::before {
  left: -5px;
}

.emblem-outer::after {
  right: -5px;
}

.emblem-inner {
  position: absolute;
  top: 15%;
  left: 15%;
  width: 70%;
  height: 70%;
  background: rgba(29, 30, 38, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
  z-index: 3;
}

.emblem-icon {
  width: 60%;
  height: 60%;
  background: linear-gradient(135deg, #8f0000, #690000);
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
  transform: rotate(45deg);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: rotate(45deg) scale(1); }
  50% { transform: rotate(45deg) scale(1.1); }
}

/* Media queries for separator */
@media (max-width: 768px) {
  .section-separator {
    height: 80px;
    margin-top: -40px;
  }
  
  .separator-line {
    width: 20%;
  }
  
  .separator-emblem {
    width: 60px;
    height: 60px;
  }
}

/* Existing styles remain unchanged */
.take-a-tour-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
  perspective: 1000px;
}

/* Floating image collage replacing carousel */
.floating-images {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
}

.floating-image {
  position: absolute;
  width: 300px;
  height: 200px;
  background-size: cover;
  background-position: center;
  border: 3px solid rgba(143, 0, 0, 0.7);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  animation: float 15s infinite ease-in-out;
  opacity: 0.6;
  will-change: transform;
  /* Remove the transition to prevent initial jerky movement */
  /* transition: all 0.2s ease; */
}

/* Position each floating image - keep the positioning, remove the transform */
.image-0 {
  top: 10%;
  left: -5%;
  z-index: 3;
  transform: rotate(-5deg) scale(0.8);
}

.image-1 {
  top: 5%;
  right: -2%;
  z-index: 2;
  transform: rotate(3deg) scale(0.7);
}

.image-2 {
  bottom: 15%;
  left: -8%;
  z-index: 3;
  transform: rotate(8deg) scale(0.75);
}

.image-3 {
  bottom: 10%;
  right: -5%;
  z-index: 2;
  transform: rotate(-7deg) scale(0.7);
}

.image-4 {
  top: 50%;
  left: 5%;
  z-index: 2;
  transform: rotate(5deg) scale(0.6) translateY(-50%);
}

.image-5 {
  top: 40%;
  right: 2%;
  z-index: 3;
  transform: rotate(-4deg) scale(0.65) translateY(-50%);
}

.image-6 {
  top: 75%;
  right: 15%;
  z-index: 2;
  transform: rotate(6deg) scale(0.55);
}

/* Update the float animation to start from the current transform */
@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(var(--rotation, 0deg)); }
  50% { transform: translate(0, -20px) rotate(var(--rotation, 0deg)); }
}

/* Main content container with glassmorphism */
.content-container {
  position: relative;
  width: 70%;
  max-width: 900px;
  z-index: 10;
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0;
  transform: translateY(30px);
  will-change: transform, opacity;
}

.content-container.visible {
  opacity: 1;
  transform: translateY(0);
}

.glassmorphism-card {
  position: relative;
  padding: 2.5rem;
  background: rgba(29, 30, 38, 0.5);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 24px;
  border: 2px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* Card corner accents */
.card-accent {
  position: absolute;
  width: 40px;
  height: 40px;
  background: transparent;
}

.top-left {
  top: 0;
  left: 0;
  border-top: 4px solid #8f0000;
  border-left: 4px solid #8f0000;
  border-top-left-radius: 8px;
}

.top-right {
  top: 0;
  right: 0;
  border-top: 4px solid #8f0000;
  border-right: 4px solid #8f0000;
  border-top-right-radius: 8px;
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 4px solid #8f0000;
  border-left: 4px solid #8f0000;
  border-bottom-left-radius: 8px;
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 4px solid #8f0000;
  border-right: 4px solid #8f0000;
  border-bottom-right-radius: 8px;
}

/* Gradient overlay */
.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg, 
    rgba(143, 0, 0, 0.1) 0%, 
    rgba(143, 0, 0, 0.05) 50%,
    rgba(30, 30, 40, 0.2) 100%
  );
  pointer-events: none;
}

.content-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  z-index: 2;
}

.title {
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  margin: 0;
}

.subtitle {
  font-size: 1.25rem;
  color: rgba(183, 185, 186, 0.9);
  margin: 0;
  max-width: 80%;
}

/* Video preview styling */
.video-preview {
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 Aspect ratio */
  position: relative;
  margin: 1rem 0;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform;
}

.video-preview:hover {
  transform: scale(1.02);
}

.video-thumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
}

.video-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
}

/* Play button styling */
.play-button-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
}

.play-button-outer {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(143, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s infinite;
}

.play-button-inner {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(143, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.play-icon {
  color: white;
  font-size: 1.5rem;
  margin-left: 5px;
}

.video-preview:hover .play-button-inner {
  transform: scale(1.1);
}

/* CTA Button styling */
.cta-button {
  position: relative;
  padding: 0.8rem 2rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  color: white;
  overflow: hidden;
  border: 2px solid rgba(143, 0, 0, 0.7);
  border-radius: 6px;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.button-text {
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.button-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.7), rgba(173, 0, 0, 0.9));
  z-index: 1;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.button-text.button-hovered {
  letter-spacing: 2px;
}

.button-bg-animate {
  transform: scaleX(1);
}

.cta-button:hover {
  box-shadow: 0 5px 15px rgba(143, 0, 0, 0.4);
}

/* Steel accent elements */
.steel-accent {
  position: absolute;
  background: linear-gradient(135deg, rgba(183, 185, 186, 0.3), rgba(100, 100, 110, 0.1));
  z-index: 5;
}

.steel-accent.left {
  top: 30%;
  left: 0;
  width: 40px;
  height: 40%;
  clip-path: polygon(0 0, 100% 20%, 100% 80%, 0 100%);
}

.steel-accent.right {
  top: 20%;
  right: 0;
  width: 40px;
  height: 60%;
  clip-path: polygon(0 20%, 100% 0, 100% 100%, 0 80%);
}

.steel-accent.top {
  top: 0;
  left: 20%;
  width: 60%;
  height: 30px;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 20% 100%);
}

.steel-accent.bottom {
  bottom: 0;
  left: 30%;
  width: 40%;
  height: 25px;
  clip-path: polygon(20% 0, 80% 0, 100% 100%, 0 100%);
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 10, 15, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.modal-content {
  position: relative;
  width: 85%;
  height: 85%;
  max-width: 1200px;
  background: #1d1e26;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform: scale(0.9);
  opacity: 0;
  animation: modalEnter 0.4s forwards cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.video-container {
  width: 100%;
  height: 100%;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(143, 0, 0, 0.8);
  color: white;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(173, 0, 0, 1);
  transform: scale(1.1);
}

/* Animations */
@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(var(--rotation, 0deg)); }
  50% { transform: translate(0, -20px) rotate(var(--rotation, 0deg)); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 0.9; }
}

@keyframes modalEnter {
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

/* Modal transitions */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Media queries */
@media (max-width: 1024px) {
  .content-container {
    width: 85%;
  }
  
  .title {
    font-size: 2rem;
  }
  
  .floating-image {
    width: 250px;
    height: 170px;
  }
}

@media (max-width: 768px) {
  .take-a-tour-container {
    height: 100vh;
  }
  
  .content-container {
    width: 90%;
  }
  
  .glassmorphism-card {
    padding: 1.5rem;
  }
  
  .title {
    font-size: 1.8rem;
  }
  
  .subtitle {
    font-size: 1rem;
    max-width: 95%;
  }
  
  .floating-image {
    width: 180px;
    height: 120px;
    opacity: 0.4;
  }
  
  .play-button-outer {
    width: 60px;
    height: 60px;
  }
  
  .play-button-inner {
    width: 40px;
    height: 40px;
  }
  
  .steel-accent {
    display: none;
  }
}
</style>
